/**
 * 会社全メンバーの gb74 個人スコアから組織レポートデータ(OrgReportData)を構築する（実データ版）。
 *
 * deriveIndividualFactors で個人サブスコアを導出し、orgScore.ts の確定集約ロジックで
 * 3スコア・総合評価・組織ダイヤモンド・散布図・部署別ヒート・§03つながり構造を算出する。
 *
 * §03(つながりの構造): 基本ONAには部署×部署の往来マトリクスが無いため、ここでは
 *   「部署別のつながり健全度」から △/∨ を分類する（トポロジカルなハブ検出ではなく健全度ベース）。
 *   ネームジェネレータ等で実マトリクスが得られれば deptConnectionStructure(レベルA) に差し替える。
 * Priority Index(改善施策): 本番のAI生成は未実装のため、弱点カテゴリからのテンプレート。
 */
import {
  getAxisScores,
  getFactorScore,
  BIG5_FACTORS,
  type AxisKey,
  type Gb74Scores,
} from './gb74Report';
import {
  aggregateWellbeing,
  aggregateConnection,
  aggregateRetention,
  aggregateOverall,
  median,
  toGrade,
  toScore100,
  type ConnectionVars,
  type Grade,
} from './orgScore';
import { deriveIndividualFactors } from './gb74OrgFactors';
import { classifyOrgArchetype } from './orgArchetypes';
import { computeHerrmannLoad, toLoadDisplay } from './herrmannLoad';
import type {
  OrgReportData,
  ScatterDot,
  DeptHeatRow,
  ImprovementItem,
  OrgGauge,
} from './orgReportDummy';
import type { DeptConnectionStructure, DeptNode, DeptRole } from './deptConnectionStructure';
import type { TeamJudgmentPattern } from '../../admin/api/teamJudgmentPatternApi';

/** 入力: 1メンバーの結果 */
export interface OrgMemberResult {
  scores: Gb74Scores;
  /** 部署名 (未設定は「(部署未設定)」) */
  departmentName?: string | null;
}

export interface BuildOrgReportOptions {
  companyName?: string;
  meta?: string;
  /** 部署別表示の最小人数 (匿名性保護、既定7) */
  minDeptMembers?: number;
  /**
   * 組織タイプ (team_judgment_patterns) の該当パターン。
   * classifyOrgType のコードで findPatternByCode 取得して渡す。
   * 指定時は §01〜§04 のリード文を **DB (tdesc11〜27) を唯一源に** 差し替える
   * (テンプレは不使用)。tdesc が空の節はブランク表示 (未整備を可視化)。
   */
  teamPattern?: TeamJudgmentPattern | null;
  /**
   * ハーマン・コミュニケーション負荷でつながりスコアを補正するか (設計ドラフト・レベル3仮説)。
   * 既定 false (基礎3スコア確定後に搭載する方針)。true でつながり調整値に負荷nudgeを掛ける。
   */
  applyHerrmannLoad?: boolean;
}

/** 緩衝力: 配慮性・感情制御の平均 (1.0〜5.0)。両方欠損は null */
function memberBuffer(scores: Gb74Scores): number | null {
  const keys = ['consideration', 'emotionControl'];
  const vals = keys
    .map((k) => {
      const fac = BIG5_FACTORS.find((f) => f.key === k);
      return fac ? getFactorScore(scores, fac) : 0;
    })
    .filter((v) => v > 0);
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
}

/** 非空の tdesc を改行で連結 (空なら空文字) */
function joinTdesc(...parts: Array<string | null | undefined>): string {
  return parts
    .map((p) => (p ?? '').trim())
    .filter(Boolean)
    .join('\n');
}

const DEPT_PALETTE = ['#3B82F6', '#EC4899', '#8B5CF6', '#22C55E', '#D97706', '#0EA5E9', '#EF4444'];
const NO_DEPT = '(部署未設定)';

/**
 * 個人軸(0〜12) → 散布図座標。レーダー(DiamondChart)と同じ四隅配置に合わせる:
 *   左上=A論理 / 右上=D創造 / 右下=C共感 / 左下=B着実。
 * 各隅を重みA/B/C/Dとする重心。x=(C+D)/Σ(右寄り), y=(B+C)/Σ(下寄り)。
 */
function axisToScatter(axis: Record<AxisKey, number>): { x: number; y: number } {
  const sum = axis.A + axis.B + axis.C + axis.D;
  if (sum <= 0) return { x: 0.5, y: 0.5 };
  const x = (axis.C + axis.D) / sum;
  const y = (axis.B + axis.C) / sum;
  return { x: Math.max(0.04, Math.min(0.96, x)), y: Math.max(0.04, Math.min(0.96, y)) };
}

const GAUGE_KEY_LABEL: Record<'wellbeing' | 'connection' | 'retention', string> = {
  wellbeing: '働きがい',
  connection: 'つながり',
  retention: '離職の兆し',
};

function medianConnVars(list: ConnectionVars[]): ConnectionVars {
  const pick = (k: keyof ConnectionVars) => median(list.map((v) => v[k]));
  return {
    help: pick('help'),
    vdep: pick('vdep'),
    boss: pick('boss'),
    silo: pick('silo'),
    bridge: pick('bridge'),
  };
}

/** 部署別のつながり健全度から §03 構造(健全度ベース)を作る */
function buildConnectionStructure(
  deptFactors: Array<{ dept: string; connScore: number; vdepScore: number }>
): DeptConnectionStructure {
  if (deptFactors.length === 0) {
    return {
      nodes: [],
      triangles: [],
      bottleneckDept: null,
      isolatedDepts: [],
      summary: 'つながりの構造を表示できる部署がありません。',
    };
  }
  // 最も弱い部署(つながりスコア最低)をボトルネック候補に
  const sorted = [...deptFactors].sort((a, b) => a.connScore - b.connScore);
  const weakest = sorted[0];
  const bottleneckDept = weakest.connScore < 50 ? weakest.dept : null;
  const nodes: DeptNode[] = deptFactors.map((d) => {
    let role: DeptRole;
    if (bottleneckDept && d.dept === bottleneckDept) role = 'bottleneck';
    else if (d.connScore >= 60) role = 'triangle';
    else role = 'spoke';
    return {
      dept: d.dept,
      degree: Math.round((d.connScore / 100) * 100) / 100, // つながり健全度を degree バーに流用
      betweenness: bottleneckDept && d.dept === bottleneckDept ? 1 : 0,
      role,
    };
  });
  const summary = bottleneckDept
    ? `${bottleneckDept}のつながりが相対的に弱く、特定の人への集中(V字)が起きやすい状態です。仕事のやり方を共有し、三角形を増やすと安定します。`
    : '部署ごとのつながりは概ね健全で、誰かが抜けてもカバーし合える状態です。';
  return { nodes, triangles: [], bottleneckDept, isolatedDepts: [], summary };
}

/** 改善施策のベース文言(弱点カテゴリ駆動)。単一版とフォールバック3件版で共有する。 */
const IMPROVEMENT_BASE: Record<
  'wellbeing' | 'connection' | 'retention',
  Omit<ImprovementItem, 'rank' | 'rankColor'>
> = {
  connection: {
    title: '特定の人への集中（V字）を「三角形」に変える',
    reason:
      '相談が一部の人に集中している兆候があります。仕事のやり方を共有し、3人で相談し合える三角形を増やすと構造的に安定します。',
    action: '頼られている人の仕事のやり方を、メモでいいので1つだけ書き出してもらう',
    effect: 'つながりスコアの底上げが見込めます',
  },
  retention: {
    title: '若手の「成長実感」を支える場を作る',
    reason:
      '離職の兆しスコアが相対的に低めです。月1回の学びの場で先輩のコツを共有すると定着につながります。',
    action: '入社2〜3年目の社員に「最近、新しく学んだことある？」と聞いてみる',
    effect: '離職の兆しスコアの改善が見込めます',
  },
  wellbeing: {
    title: '働きがいの土台を整える',
    reason: '働きがいスコアが相対的に低めです。役割と裁量を少しずつ広げると基盤が上がります。',
    action: '承認や判断を1つだけ部下に任せてみる',
    effect: '働きがいスコアの改善が見込めます',
  },
};
const IMPROVEMENT_RANK_COLORS = ['#B33A3A', '#B8860B', '#2F6FB3'];

/** デモ用テンプレートと同じ改善施策(弱点カテゴリ駆動・単一) */
function buildImprovements(weakest: 'wellbeing' | 'connection' | 'retention'): ImprovementItem[] {
  return [{ rank: 1, rankColor: IMPROVEMENT_RANK_COLORS[0], ...IMPROVEMENT_BASE[weakest] }];
}

/**
 * 会社メンバーの結果配列から OrgReportData を構築する。
 */
export function buildOrgReportFromResults(
  members: OrgMemberResult[],
  opts: BuildOrgReportOptions = {}
): OrgReportData {
  const minDept = opts.minDeptMembers ?? 7;
  const factors = members.map((m) => ({
    ...deriveIndividualFactors(m.scores),
    dept: m.departmentName || NO_DEPT,
  }));
  const n = Math.max(1, factors.length);

  // ---- 3スコア集約 ----
  const wellbeingResult = aggregateWellbeing(factors.map((f) => f.wellbeing));
  let connectionResult = aggregateConnection(medianConnVars(factors.map((f) => f.connVars)));
  const retentionResult = aggregateRetention(factors.map((f) => f.retention));

  // ---- ハーマン負荷でつながりスコアを補正 (設計ドラフト・既定OFF) ----
  const applyLoad = opts.applyHerrmannLoad === true;
  const load = computeHerrmannLoad(
    members.map((m) => ({ axis: getAxisScores(m.scores), buffer: memberBuffer(m.scores) }))
  );
  const connBefore = connectionResult.score;
  const loadApplied = applyLoad && load.applicable && load.nudge > 0;
  if (loadApplied) {
    const adj = connectionResult.adjusted * (1 - load.nudge);
    const score = toScore100(adj);
    const { grade, weather } = toGrade(score);
    connectionResult = { ...connectionResult, adjusted: adj, score, grade, weather };
  }

  const overall = aggregateOverall(wellbeingResult, connectionResult, retentionResult);

  // ---- 組織ダイヤモンド (4軸平均) ----
  const axis: Record<AxisKey, number> = { A: 0, B: 0, C: 0, D: 0 };
  (['A', 'B', 'C', 'D'] as AxisKey[]).forEach((k) => {
    axis[k] = Math.round((factors.reduce((s, f) => s + (f.axis[k] ?? 0), 0) / n) * 10) / 10;
  });
  const AXIS_LABEL: Record<AxisKey, string> = { A: 'システム', B: '安定', C: '人', D: '変化' };
  const dominant = (['A', 'B', 'C', 'D'] as AxisKey[]).reduce((a, b) =>
    axis[a] >= axis[b] ? a : b
  );

  // ---- 部署→色 ----
  const deptNames = Array.from(new Set(factors.map((f) => f.dept)));
  const deptColor: Record<string, string> = {};
  deptNames.forEach((d, i) => (deptColor[d] = DEPT_PALETTE[i % DEPT_PALETTE.length]));

  // ---- 散布図 ----
  const scatter: ScatterDot[] = factors.map((f) => {
    const { x, y } = axisToScatter(f.axis);
    // axis を保持し、散布図で各個人のミニ・ダイヤ形を描けるようにする
    return { dept: f.dept, color: deptColor[f.dept], x, y, axis: f.axis };
  });

  // ---- 部署別 (minDept以上のみ) ----
  const heatRows: DeptHeatRow[] = [];
  const deptConnForStructure: Array<{ dept: string; connScore: number; vdepScore: number }> = [];
  for (const dept of deptNames) {
    if (dept === NO_DEPT) continue;
    const members2 = factors.filter((f) => f.dept === dept);
    if (members2.length < minDept) continue;
    const wb = aggregateWellbeing(members2.map((m) => m.wellbeing));
    const ret = aggregateRetention(members2.map((m) => m.retention));
    const conn = aggregateConnection(medianConnVars(members2.map((m) => m.connVars)));
    const helpGrade = toGrade(toScore100(median(members2.map((m) => m.connVars.help)))).grade;
    const vdepGrade = toGrade(toScore100(median(members2.map((m) => m.connVars.vdep)))).grade;
    heatRows.push({
      dept,
      cells: [
        { label: '働きがい', grade: wb.grade as Grade },
        { label: '助け合い', grade: helpGrade as Grade },
        { label: '頼りすぎ', grade: vdepGrade as Grade },
        { label: '離職の兆し', grade: ret.grade as Grade },
      ],
    });
    deptConnForStructure.push({
      dept,
      connScore: conn.score,
      vdepScore: conn.perVar.vdep.score,
    });
  }

  // ---- ゲージ ----
  const weakLabel = GAUGE_KEY_LABEL[overall.weakest];
  const gauges: OrgGauge[] = [
    {
      label: '働きがい',
      score: wellbeingResult.score,
      grade: wellbeingResult.grade,
      emphasis: overall.weakest === 'wellbeing',
    },
    {
      label: 'つながり',
      score: connectionResult.score,
      grade: connectionResult.grade,
      emphasis: overall.weakest === 'connection',
    },
    {
      label: '離職の兆し',
      score: retentionResult.score,
      grade: retentionResult.grade,
      emphasis: overall.weakest === 'retention',
    },
  ];

  const connectionStructure = buildConnectionStructure(deptConnForStructure);

  const report: OrgReportData = {
    companyName: opts.companyName ?? '会社全体',
    meta: opts.meta ?? `${members.length}名の回答に基づく集計`,
    totalScore: overall.score,
    totalGrade: overall.grade,
    evaluationLead:
      overall.grade === 'A' || overall.grade === 'B'
        ? '全体的に良好な状態です。強みを言語化して共有すると、さらに安定します。'
        : `一部に注意が必要な状態です。特に「${weakLabel}」の改善が、全体を良くする最短の道筋です。`,
    axis,
    axisNote: `${AXIS_LABEL[dominant]}の力が組織全体で相対的に高い構成です。`,
    gauges,
    herrmannLoad: applyLoad
      ? toLoadDisplay(load, connBefore, connectionResult.score, loadApplied)
      : undefined,
    distributionLead: '各メンバーの回答傾向を、システム↔人・安定↔変化の2軸で配置したものです。',
    scatter,
    deptLegend: deptNames
      .filter((d) => d !== NO_DEPT)
      .map((d) => ({ dept: d, color: deptColor[d] })),
    connectionLead: connectionStructure.summary,
    connectionStructure,
    improvementLead: `今の総合評価は${overall.grade}（${overall.score}点）。以下に取り組むと、改善が見込めます。`,
    improvementTarget: Math.min(100, overall.score + 11),
    improvements: buildImprovements(overall.weakest),
    closingText:
      'このレポートは会社全体の回答を集計したものです。\nスコア（働きがい・つながり・離職の兆し・総合評価）は確定集約ロジックで算出しています。',
    heatHeader: ['働きがい', '助け合い', '頼りすぎ', '離職の兆し'],
    heatRows,
  };

  // ===== 12原型の確定文言を適用 =====
  // 思考傾向(A/B/C/D/X/P) × 健全度(G/R) で1原型に分類し、各節のリード文と改善提案を
  // GB組織タイプ12分類の文言に差し替える。算出した数値・分布・図はそのまま使う。
  // これにより「今月やること」まで含む具体的な文面が出せる（従来は機械的な組み立て文）。
  const arche = classifyOrgArchetype(
    axis,
    {
      wellbeing: wellbeingResult.score,
      connection: connectionResult.score,
      retention: retentionResult.score,
    },
    members.length
  ).archetype;

  report.orgArchetypeId = arche.id;
  report.orgArchetypeName = arche.name;
  report.orgArchetypeCharacter = arche.character;
  report.evaluationLead = arche.evaluationLead;
  report.axisNote = arche.axisNote;
  report.distributionLead = arche.distributionLead;
  report.connectionLead = arche.connectionLead;
  report.improvementLead = arche.improvementLead;
  report.improvements = arche.improvements.map((a, i) => ({
    rank: i + 1,
    rankColor: IMPROVEMENT_RANK_COLORS[i] ?? IMPROVEMENT_RANK_COLORS[0],
    title: a.title,
    reason: a.body,
    action: a.thisMonth,
    effect: '',
  }));

  // ===== DB結線: 組織タイプパターン(tdesc) を唯一源に §01〜§04 のリード文を差し替え =====
  // 数値・分布・ヒート等の算出値は維持し、各節の「文言リード」のみ tdesc に切替える。
  // tdesc が空(DB未投入)の節は、算出値ベースのデフォルト文言へフォールバックし空欄を出さない。
  if (opts.teamPattern) {
    const p = opts.teamPattern;
    // §04 改善施策(tdesc21〜27)がDB未投入なら、3スコアの弱い順3件へフォールバック
    // tdesc21〜26 が未投入なら 12原型の確定文言（report.improvements）を使う。
    // 以前は算出ロジックの組み立て文へ落ちていたが、原型の方が具体的で質が高い。
    const tdescImprovements = buildTdescImprovements(p);
    const improvements = tdescImprovements.length > 0 ? tdescImprovements : report.improvements;
    return {
      ...report,
      // §01 総合評価: 総括(tdesc19) をリードに、組織の特性(tdesc11) を軸ノートに（空なら算出デフォルト）
      evaluationLead: joinTdesc(p.tdesc19) || report.evaluationLead,
      axisNote: joinTdesc(p.tdesc11, p.tdesc12) || report.axisNote,
      // §02 分布: 軸の偏り(tdesc13/14)
      distributionLead: joinTdesc(p.tdesc13, p.tdesc14) || report.distributionLead,
      // §03 つながり: 構造課題(tdesc15/16) + 負荷偏り(tdesc17/18)
      connectionLead:
        joinTdesc(p.tdesc15, p.tdesc16, p.tdesc17, p.tdesc18) || report.connectionLead,
      // §04 改善施策: tdesc27 が空なら算出値ベースのリード文へフォールバック
      // tdesc27 が空なら 12原型のリード文へ（算出フォールバックより優先）
      improvementLead: joinTdesc(p.tdesc27) || report.improvementLead,
      improvements,
    };
  }

  return report;
}

/**
 * §04 改善施策カードを tdesc21〜27 から構築する。
 * tdesc2x が未投入(空)の場合は空配列を返す (テンプレにフォールバックせず、未整備を可視化)。
 *   tdesc21/22=強みを活かす(テーマ/アドバイス), tdesc23/24=弱みを克服, tdesc25/26=パフォーマンス向上
 */
function buildTdescImprovements(p: TeamJudgmentPattern): ImprovementItem[] {
  const defs: Array<{ theme?: string | null; advice?: string | null; color: string }> = [
    { theme: p.tdesc21, advice: p.tdesc22, color: '#B33A3A' },
    { theme: p.tdesc23, advice: p.tdesc24, color: '#B8860B' },
    { theme: p.tdesc25, advice: p.tdesc26, color: '#2F6FB3' },
  ];
  const items: ImprovementItem[] = [];
  defs.forEach((d, i) => {
    const title = (d.theme ?? '').trim();
    const reason = (d.advice ?? '').trim();
    if (!title && !reason) return; // 未投入はスキップ
    items.push({
      rank: i + 1,
      rankColor: d.color,
      title,
      reason,
      action: '',
      effect: '',
    });
  });
  return items;
}
